import { Loader2 } from "lucide-react";
import { useState } from "react";
import {
  useCopyDrawingToolData,
  useGetProductSalesUnitsSavedDrawing
} from "../../api/drawingTool";
import { DropDown } from "../../components";
import { Button } from "../../components/ui/common/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../components/ui/common/dialog";
import { MappingSourceType } from "../../config/enums/MappingSourceType.enum";
import {
  mappingDialogBodyClass,
  mappingDialogContentClass,
  mappingDialogDropdownClass,
  mappingDialogFooterClass,
  mappingDialogHeaderClass,
  mappingDialogLoadingOverlayClass
} from "./mappingModalStyles";

const ImportMappingModal = ({
  viewModal = false,
  closeModal = () => {},
  ownerId,
  sourceId,
  onSuccessCopy
}: any) => {
  const [searchKey, setSearchKey] = useState("");
  const [toCopyData, setToCopyData] = useState<any>();
  const [selectedDrawingId, setSelectedDrawingId] = useState<string>("");
  const [isError, setIsError] = useState(false);

  const { data: salesUnits, isLoading: isSalesUnitsLoading } =
    useGetProductSalesUnitsSavedDrawing({
      mappingType: MappingSourceType.product,
      searchKey
    });

  const salesUnitsValues: DropdownOption[] = (salesUnits || []).map(
    ({ productName, salesUnitName, salesUnitId, productId }) => ({
      key: salesUnitId,
      text: `${productName} - ${salesUnitName}`,
      value: salesUnitId,
      product: productId
    })
  );

  const { mutate: copyDrawing, isLoading: isLoadingCopyDrawing } =
    useCopyDrawingToolData(MappingSourceType.product);

  const pasteDrawing = () => {
    if (!selectedDrawingId) {
      setIsError(true);
      return;
    }
    setIsError(false);
    copyDrawing(
      {
        drawingId: selectedDrawingId,
        ownerId: ownerId,
        sourceId: sourceId
      },
      {
        onSuccess: () => {
          closeModal();
          onSuccessCopy(sourceId);
        },
        onError: () => {
          closeModal();
        }
      }
    );
  };

  return (
    <Dialog open onOpenChange={open => !open && closeModal()}>
      <DialogContent
        className={mappingDialogContentClass}
        onOpenAutoFocus={event => event.preventDefault()}
      >
        <DialogHeader className={mappingDialogHeaderClass}>
          <DialogTitle>Import Mapping!</DialogTitle>
        </DialogHeader>

        <div className={`${mappingDialogBodyClass} ${mappingDialogDropdownClass}`}>
          {isLoadingCopyDrawing ? (
            <div className={mappingDialogLoadingOverlayClass}>
              <Loader2 className="tw-h-6 tw-w-6 tw-animate-spin tw-text-primary" />
            </div>
          ) : null}

          <DropDown
            currentData={salesUnitsValues}
            defaultValue={toCopyData?.value}
            handleChangeState={(e: any, data: any) => {
              const drawingId = salesUnits?.find(item => item.salesUnitId === data.value)?.drawingId;
              const values = salesUnitsValues.find(ps => ps.key === data.value);
              setToCopyData(values);
              setSelectedDrawingId(drawingId || "");
              setIsError(false);
            }}
            onSearchChange={(e: any, data: any) => {
              setSearchKey(data.searchQuery);
            }}
            loading={isSalesUnitsLoading}
            search={options => options}
            showNoResults={false}
            minSearchLength={1}
            defaultOpen={false}
            placeholder="Search product..."
          />
          <p className="tw-m-0 tw-text-sm tw-text-muted-foreground">
            Please select the product you want to import the mapping available from the dropdown.
          </p>
          {isError ? (
            <p className="tw-m-0 tw-text-sm tw-text-destructive">
              Please select a product to import the mapping.
            </p>
          ) : null}
        </div>

        <DialogFooter className={mappingDialogFooterClass}>
          <Button
            type="button"
            variant="outline"
            disabled={isLoadingCopyDrawing}
            onClick={() => closeModal()}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isLoadingCopyDrawing || !selectedDrawingId}
            onClick={() => pasteDrawing()}
          >
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportMappingModal;
